import { Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { getTaskDefaultDaysBefore, getTaskLabel } from 'app/events/helpers/event-task-helpers';
import { invalidateApolloCacheFor } from 'core/apollo-cache-invalidator';
import { ToastrService } from 'core/toastr.service';
import { differenceInCalendarDays } from 'date-fns';
import { CalendarEventActionPurposeEnum, CalendarEventInput, CreateCalendarEventGQL, LinkEventToActionNetworkGQL } from 'generated/graphql';
import { finalize } from 'rxjs';
import { EventFormComponent } from '../../components/event-form/event-form.component';
import { AnEventSyncOptionsComponent } from '../../components/an-event-sync-options/an-event-sync-options.component';

@Component({
  selector: 'app-add-event-dialog',
  templateUrl: `./add-event.dialog.html`,
})
export class AddEventDialog  {
  @ViewChild('eventFormRef') eventFormRef: EventFormComponent;
  @ViewChild(AnEventSyncOptionsComponent) eventSyncComponent: AnEventSyncOptionsComponent;

  static open(dialog: MatDialog, config: MatDialogConfig<any>) {
    return dialog.open(AddEventDialog, config);
  }

  calendarEvent: CalendarEventInput | null = null;
  showMultiple = false;
  isSubmitting = false;
  errorMessages: string[] = [];

  taskForm = new FormGroup({
    [CalendarEventActionPurposeEnum.Confirm]: new FormControl<boolean>(true),
    [CalendarEventActionPurposeEnum.DoubleConfirm]: new FormControl<boolean>(true),
    [CalendarEventActionPurposeEnum.LogAttendance]: new FormControl<boolean>(true),
  });

  constructor(
    private _createCalendarEventGQL: CreateCalendarEventGQL,
    private _linkEventToActionNetworkGQL: LinkEventToActionNetworkGQL,
    public dialogRef: MatDialogRef<AddEventDialog>,
    private _route: ActivatedRoute,
    private _router: Router,
    private _toastrService: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data && data.eventDetails) {
      this.calendarEvent = data.eventDetails;
    }
  }

  getCreateTaskLabel(actionPurpose: CalendarEventActionPurposeEnum): string {
    const daysBefore = getTaskDefaultDaysBefore(actionPurpose);
    const label = getTaskLabel(actionPurpose);
    if (daysBefore === 0) { return `${label} on day of event`; }
    return `${label} ${daysBefore} days before event`;
  }

  close() {
    this.dialogRef.close();
  }


  calendarEventUpdated(event: CalendarEventInput) {
    this.calendarEvent = event;
  }

  startDateChanged(date: Date) {
    this.updateTaskOptions(date);
  }

  updateTaskOptions(startDate) {
    // Update task toggles based on start date
    const daysUntilStartDate = differenceInCalendarDays(new Date(startDate), new Date()) - 1;

    // Disable tasks where default auto-create date is in past
    for (const taskType in CalendarEventActionPurposeEnum) {
      if (this.taskForm.get(taskType)) {
        const daysBefore = getTaskDefaultDaysBefore(CalendarEventActionPurposeEnum[taskType]);
        if (daysBefore > daysUntilStartDate) {
          this.taskForm.get(taskType).setValue(false);
          this.taskForm.get(taskType).disable();
        } else {
          this.taskForm.get(taskType).setValue(true);
          this.taskForm.get(taskType).enable();
        }
      }
    }
  }

  updateTasks() {
    for (const taskType in CalendarEventActionPurposeEnum) {
      if (taskType !== CalendarEventActionPurposeEnum.Recruit) {
        this.calendarEvent[`action${taskType}ScheduledDays`] =
          this.taskForm.get(taskType).value ?
            getTaskDefaultDaysBefore(CalendarEventActionPurposeEnum[taskType])
            : null;
      }
    }
  }

  createMultipleEventsClicked() {
    this.isSubmitting = true;
    this.errorMessages = [];
    const { isValid, errorMessages } = this.eventFormRef.validate();
    if (!isValid) {
      this.errorMessages = errorMessages;
      this.isSubmitting = false;
    } else {
      this.updateTasks();
      this.isSubmitting = false;
      this.showMultiple = true;
    }
  }

  /**
   * Getter for enableEventSync - checks if sync is enabled in the child component
   * Used to disable "Create multiple events" button
   */
  get enableEventSync(): boolean {
    return this.eventSyncComponent?.isSyncEnabled ?? false;
  }

  createEventClicked() {
    this.isSubmitting = true;
    this.errorMessages = [];
    const { isValid, errorMessages } = this.eventFormRef.validate();
    if (!isValid) {
      this.errorMessages = errorMessages;
      this.isSubmitting = false;
    } else {
      this.updateTasks();

      // Get selected sync event, if any
      const selectedANEvent = this.eventSyncComponent?.getSelectedANEvent();

      this._createCalendarEventGQL
        .mutate({
          input: { calendarEvent: this.calendarEvent },
        }, {
          update: (cache) => {
            invalidateApolloCacheFor(cache, c => c.fieldName?.includes('searchCalendarEvents'));
          }
        })
        .pipe(
          finalize(() => {
            if (!selectedANEvent) this.isSubmitting = false;
          })
        )
        .subscribe(({ data }) => {
          const createdEventId = data.createCalendarEvent.calendarEvent.id;

          if (selectedANEvent) {
            this._linkEventToActionNetworkGQL
              .mutate({
                calendarEventId: createdEventId,
                actionNetworkEventId: Number(selectedANEvent.id),
              })
              .subscribe(() => {
                this.isSubmitting = false;
                this.redirectToEventPage(createdEventId);
              });
          } else {
            this.redirectToEventPage(createdEventId);
          }
        });
    }
  }

  redirectToEventPage(createdEventId: string) {
    this._router
      .navigate(['/events/view'], {
        queryParams: {
          campaignId: this._route.snapshot.queryParams.campaignId,
          eventId: createdEventId,
        },
      })
      .then(() => {
        this.close();
      });
  }

  eventsCreated(_data: any) {
    this._toastrService.success('Events created successfully');
    this.close();
  }
}
